import React, { useEffect, useRef, useState } from 'react';
import {
  FiMic,
  FiSettings,
  FiMicOff,
  FiVideo,
  FiVideoOff,
  FiMonitor,
  FiEdit3,
  FiUsers,
  FiMessageSquare,
  FiMessageCircle,
} from 'react-icons/fi';
import { MdStopScreenShare } from 'react-icons/md';
import Loader from 'pages/Test/localComponent/loader';
import '../broadcastNext.css';

// Highest first, the order every player lists them in, with Auto on top as the default.
// value is the ladder index setVideoQuality expects; null is Auto.
const QUALITY_OPTIONS = [
  { value: null, label: 'Auto' },
  { value: 2, label: '720p' },
  { value: 1, label: '480p' },
  { value: 0, label: '360p' },
];

/**
 * Quality picker, in the shape people already know from a video player.
 *
 * Every label here is measured, never assumed. activeLabel comes from the frame size in the
 * stats sample — what the encoder is actually producing, or what the decoder is actually
 * receiving — so the button answers "what am I really at" rather than repeating the last
 * thing that was clicked.
 *
 * That distinction earns its keep when the two disagree. A pin to 720p on a line that
 * cannot carry it, or a preferred-layer request the server never implemented, both leave
 * the choice and the reality apart; the menu shows the choice as ticked and the button
 * shows what is happening, which is how you find out.
 */
const QualityMenu = ({ value = null, activeLabel = '', detail = '', direction = '', limitedBy = null, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Pointerdown rather than click: a click listener closes the menu only after the button
  // that was pressed has already handled its own click, which makes the toggle fight itself.
  useEffect(() => {
    if (!open) return undefined
    const onAway = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false)
    };
    const onEsc = (event) => { if (event.key === 'Escape') setOpen(false) };
    document.addEventListener('pointerdown', onAway);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('pointerdown', onAway);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const current = QUALITY_OPTIONS.find((option) => option.value === value) ?? QUALITY_OPTIONS[0];
  // Measured first, always. On Auto it is the whole point; on a pin it is the check that the
  // pin took — and a mismatch is exactly what someone needs to see rather than be shielded
  // from, so the button reports the truth and the tick keeps showing the request.
  const buttonLabel = activeLabel
    ? (value === null ? `Auto ${activeLabel}` : activeLabel)
    : current.label;
  const mismatch = value !== null && activeLabel && activeLabel !== current.label;
  const title = [
    detail ? `${direction === 'receiving' ? 'Receiving' : 'Sending'} ${detail}` : 'Video quality',
    mismatch ? `Set to ${current.label}, but ${activeLabel} is what is getting through` : null,
    // libwebrtc's own word for why it is not doing better. The two causes need opposite
    // responses, so naming which one it is saves guessing.
    limitedBy === 'bandwidth' ? 'Limited by available bandwidth' : null,
    limitedBy === 'cpu' ? 'Limited by this device — close other apps' : null,
  ].filter(Boolean).join(String.fromCharCode(10));

  return (
    <div className="bn-quality-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`bn-ctrl ${open ? 'bn-ctrl-on' : ''}`}
        onClick={() => setOpen((was) => !was)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={title}
      >
        <FiSettings size={16} />
        {buttonLabel}
        {mismatch ? <span className="bn-quality-warn">!</span> : null}
      </button>

      {open ? (
        <div className="bn-quality-menu" role="menu">
          {QUALITY_OPTIONS.map((option) => (
            <button
              key={option.label}
              type="button"
              role="menuitemradio"
              aria-checked={option.value === value}
              className={`bn-quality-item ${option.value === value ? 'bn-on' : ''}`}
              onClick={() => { onChange(option.value); setOpen(false) }}
            >
              <span className="bn-quality-tick">{option.value === value ? '✓' : ''}</span>
              {option.label}
              {/* Auto is the only row that gains anything from a measurement beside it —
                  it is the one whose name does not say what you are getting. On a fixed
                  choice the row already reads 720p, and repeating the pixel count next to
                  it is noise. The exact figure stays in the button's tooltip. */}
              {option.value === null && value === null && activeLabel ? (
                <span className="bn-quality-hint">{activeLabel}</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Floating control bar for the live classroom. Every button calls the handler the old
 * ActionBoxRow called — mic/camera go through roomClient, share screen keeps the existing
 * antd popover (passed in as shareWrapper so ShareScreenPopover stays in index.js).
 */
const LiveControlBar = ({
  isStudent = false,
  isPublishing = 0,
  audioOnly = false,
  isStopping = false,
  // The lobby asked for a camera or a mic and the class has not started publishing it yet.
  // Not the same as isPublishing 1 — nothing is moving yet, it is about to — but the bar
  // has to look the same either way, because the alternative is what it used to do: show a
  // fully live idle bar with "Camera off" on it to a teacher who had just turned their
  // camera on, for the three seconds it takes to confirm nobody else holds the room.
  startPending = false,
  transitionOwner = null,
  othersSharing = false,
  shareSwapping = false,
  micIsMuted = true,
  // Whether the Mic button is on the bar. Owned by the class (index.js), which starts it
  // from the lobby's camera choice and turns it on for good at the first Camera, Share or
  // Whiteboard press. Defaults to true so every other caller of this bar is unaffected.
  showMic = true,
  isCamOn = false,
  shareType = 'screen',
  whiteboard = false,
  onMicClick,
  onCameraClick,
  onShareClick,
  onWhiteboardClick,
  onLeave,
  onParticipantsClick,
  onChatClick,
  onGroupChatClick,
  onPrivateChatClick,
  onlineCount = 0,
  videoQuality = null,
  videoQualityLabel = '',
  videoQualityDetail = '',
  videoQualityDirection = '',
  videoLimitedBy = null,
  onVideoQualityChange,
  groupUnreadCount = 0,
  privateUnreadCount = 0,
  shareWrapper = (children) => children,
  rightView = false,
  floating = false,
}) => {
  const isLive = isPublishing === 2;
  // Waiting to start and starting are one state as far as this bar is concerned: in both
  // the controls are not yet the teacher's to press, and one of them should be saying so.
  const isConnecting = isPublishing === 1 || startPending;
  // The one thing this button is about: whether a screen is on the wire. A camera broadcast
  // and a mic-only one are both "not sharing", and neither is something this button offers
  // to stop — ending the class is End class, and turning the picture or the sound off is the
  // camera and mic buttons. It used to read "Stop stream" over a camera class, which is how
  // a teacher who joined with mic and camera on arrived in a room with no way to share their
  // screen that did not first end what they were already sending.
  const isSharingScreen = isLive && !audioOnly && shareType === 'screen';
  // The picker is open, or the track is being swapped onto the producer.
  const isSwapping = shareSwapping;
  // Another teacher is live. Mic, camera, share and board all start a broadcast when nothing
  // is published yet, and the room only holds one — so while this is true they are the
  // buttons that cannot do anything, and they leave the bar rather than sit on it greyed
  // out: a row of dead controls reads as a broken class rather than as someone else's, and
  // they come back by themselves the moment that publish stops. End class stays — this
  // teacher is in the room and has to be able to leave it — and Participants and Chat sit in
  // the right slot, which is never withdrawn. Once live none of this applies: what is
  // already on the wire stays under this teacher's control.
  const lockedOut = othersSharing && !isLive && !isConnecting;
  // The publish is moving, and this is the button that asked for it. Every control is
  // disabled through the transition either way — this only decides which one says so. Share
  // keeps the unclaimed case: a reconnect or the pre-join auto-start is nobody's click, and
  // that is the button the bar has always reported those on.
  // A claim on a button that is not on the bar is no claim at all. showMic keeps the mic
  // control off until video is on the wire, so a lobby start of mic alone would otherwise
  // grey every control and put the spinner on a button nobody can see — which is the same
  // frozen-looking bar this pending state exists to replace. Hand those back to share,
  // which is where unclaimed transitions have always been reported.
  const claimant = transitionOwner === 'mic' && !showMic ? null : transitionOwner;
  const ownsTransition = (control) =>
    isConnecting && (claimant === control || (control === 'screen' && !claimant));
  const transitionLabel = isStopping ? 'Stopping…' : 'Starting…';

  return (
    // Sits under the inset stage normally. In whiteboard mode the canvas is full-bleed, so
    // the bar floats over it and stops at the side panel's edge when that is open.
    <div
      className={`bn-control-bar ${floating ? 'bn-floating' : ''}`}
      style={floating ? { right: rightView ? 'calc(25% + 20px)' : 20 } : undefined}
    >
      {/* Empty on purpose. The bar is space-between across three slots, so this one has to
          stay for the buttons to sit where they do — it just no longer says "Camera" or
          "Not live yet" beside controls that already show both. */}
      <div className="bn-control-left" />

      <div className="bn-control-center">
        {!isStudent ? (
          <>
            {/* Everything that puts something on the wire, on the bar only while this
                teacher is the one who could put something there — see lockedOut. The
                divider belongs to this group too: with nothing on its left it would be a
                rule against the edge of the bar. */}
            {!lockedOut ? (
              <>
                {/* Enabled while idle: the mic can go live on its own, with nothing shared.
                    Present only once showMic says so — see the prop. */}
                {showMic ? (
                  <button
                    type="button"
                    className={`bn-ctrl ${isLive && !micIsMuted ? 'bn-ctrl-on' : ''}`}
                    onClick={onMicClick}
                    disabled={isConnecting}
                  >
                    {ownsTransition('mic') ? (
                      <Loader style={{ fontSize: 15 }} />
                    ) : micIsMuted ? (
                      <FiMicOff size={16} />
                    ) : (
                      <FiMic size={16} />
                    )}
                    {ownsTransition('mic') ? transitionLabel : micIsMuted ? 'Mic off' : 'Mic on'}
                  </button>
                ) : null}

                {/* Enabled while idle so it can start a camera broadcast, not only toggle one. */}
                <button
                  type="button"
                  className={`bn-ctrl ${isLive && isCamOn ? 'bn-ctrl-on' : ''}`}
                  onClick={onCameraClick}
                  disabled={isConnecting}
                >
                  {ownsTransition('camera') ? (
                    <Loader style={{ fontSize: 15 }} />
                  ) : isCamOn ? (
                    <FiVideo size={16} />
                  ) : (
                    <FiVideoOff size={16} />
                  )}
                  {ownsTransition('camera') ? transitionLabel : isCamOn ? 'Camera on' : 'Camera off'}
                </button>

                {shareWrapper(
                  <button
                    type="button"
                    className={`bn-ctrl ${isSharingScreen ? 'bn-ctrl-on' : ''}`}
                    onClick={onShareClick}
                    disabled={isConnecting || isSwapping}
                  >
                    {ownsTransition('screen') || isSwapping ? (
                      <Loader style={{ fontSize: 15 }} />
                    ) : isSharingScreen ? (
                      <MdStopScreenShare size={17} />
                    ) : (
                      <FiMonitor size={16} />
                    )}
                    {/* isSwapping is this button's own work — the picker is open or the track
                        is going onto the producer — so it reports that whoever owns the
                        publish transition. Only the publish-level Starting…/Stopping… is now
                        conditional on owning it; unowned still lands here, as before. */}
                    {ownsTransition('screen')
                      ? transitionLabel
                      : isSwapping
                        ? isSharingScreen
                          ? 'Stopping…'
                          : 'Sharing…'
                        : isSharingScreen
                          ? 'Stop sharing'
                          : 'Share screen'}
                  </button>,
                )}

                {/* Enabled while idle too — that is the state the whiteboard dialog starts
                    from. Only the in-between publishing state locks it out. */}
                <button
                  type="button"
                  className={`bn-ctrl ${whiteboard ? 'bn-ctrl-on' : ''}`}
                  onClick={onWhiteboardClick}
                  disabled={isConnecting}
                >
                  {ownsTransition('whiteboard') ? (
                    <Loader style={{ fontSize: 15 }} />
                  ) : (
                    <FiEdit3 size={16} />
                  )}
                  {/* The board is on screen, so the button is the way back out of it. */}
                  {ownsTransition('whiteboard')
                    ? transitionLabel
                    : whiteboard
                      ? 'Close board'
                      : 'Whiteboard'}
                </button>

                <span className="bn-divider" />
              </>
            ) : null}

            {/* No "Stop stream" beside these: turning mic, camera, screen and board all off
                is the same request, and the class ends the publish itself when nothing is
                left on the wire — which is what frees the room for the next teacher.

                bn-ctrl-solo widens it for the locked-out bar. With the four publisher
                controls withdrawn this is the only thing left in the centre slot, and at the
                width it takes beside them it reads as a leftover fragment of a bar rather
                than as the one action on offer. */}
            <button
              type="button"
              className={`bn-ctrl bn-ctrl-danger ${lockedOut ? 'bn-ctrl-solo' : ''}`}
              onClick={onLeave}
            >
              End class
            </button>
          </>
        ) : (
          <>
            <button type="button" className="bn-ctrl" onClick={onGroupChatClick}>
              <FiMessageSquare size={16} />
              Group chat
              {groupUnreadCount ? <span className="bn-count-pill">{groupUnreadCount}</span> : null}
            </button>
            <button type="button" className="bn-ctrl" onClick={onPrivateChatClick}>
              <FiMessageCircle size={16} />
              Private chat
              {privateUnreadCount ? (
                <span className="bn-count-pill">{privateUnreadCount}</span>
              ) : null}
            </button>
            <span className="bn-divider" />
            <button type="button" className="bn-ctrl bn-ctrl-danger" onClick={onLeave}>
              Leave
            </button>
          </>
        )}
      </div>

      <div className="bn-control-right">
        {onVideoQualityChange ? (
          <QualityMenu
            value={videoQuality}
            activeLabel={videoQualityLabel}
            detail={videoQualityDetail}
            direction={videoQualityDirection}
            limitedBy={videoLimitedBy}
            onChange={onVideoQualityChange}
          />
        ) : null}

        <button type="button" className="bn-ctrl" onClick={onParticipantsClick}>
          <FiUsers size={16} />
          <span className="bn-count-pill">{onlineCount || 0}</span>
        </button>

        {/* One chat button: it opens the side panel's Chat tab, where group and private sit
            alongside Participants in the same panel. */}
        <button type="button" className="bn-ctrl" onClick={onChatClick} title="Chat">
          <FiMessageSquare size={16} />
          {groupUnreadCount || privateUnreadCount ? <span className="bn-unread-dot" /> : null}
        </button>
      </div>
    </div>
  );
};

export default LiveControlBar;
